
import { defineComponent, ref, reactive, watch, nextTick } from 'vue'
import { $t as lang } from '@/Tool'
import { ElMessage } from 'element-plus'
import api from '@/services'
import { commonUpload } from '@/services/apis/upload-common'

export default defineComponent({
  name: 'CertificationDialog',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    authorizationRequired: {
      type: Boolean,
      default: false,
    },
    forceCorpAuth: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:visible', 'success', 'close'],
  setup(props, { emit }) {
    const submitLoading = ref(false)
    const previewDialogVisible = ref(false)
    const previewImageUrl = ref('')
    const formRef = ref()
    const businessLicenseUploading = ref(false)
    const authorLetterUploading = ref(false)
    const businessLicenseUpload = ref()
    const authorLetterUpload = ref()

    // 表单数据
    const form = reactive({
      corpName: '',
      businessLicenseUrl: '',
      authorLetterUrl: '',
    })

    // 文件列表
    const businessLicenseFileList = ref([])
    const authorLetterFileList = ref([])

    // 表单验证规则
    const rules: any = {
      corpName: [
        { required: true, message: lang('certification.form.corpNameRequired'), trigger: 'blur' },
      ],
      businessLicenseUrl: [
        {
          required: true,
          message: lang('certification.form.businessLicenseRequired'),
          trigger: 'change',
        },
      ],
    }
    if (props.authorizationRequired) {
      rules.authorLetterUrl = [
        {
          required: true,
          message: lang('certification.form.authorLetterRequired'),
          trigger: 'change',
        },
      ]
    }
    // const rules = computed(() => {
    //   const baseRules: any = {
    //     corpName: [{ required: true, message: '请输入企业名称', trigger: 'blur' }],
    //     businessLicenseUrl: [
    //       { required: true, message: '请上传营业执照', trigger: 'change' }
    //     ],
    //   }

    //   // 只有在授权书必填时才添加验证规则
    //   if (props.authorizationRequired) {
    //     baseRules.authorLetterUrl = [
    //       { required: true, message: '请上传企业授权书', trigger: 'change' }
    //     ]
    //   }

    //   return baseRules
    // })

    // 预览功能
    const handlePreview = (url: string) => {
      if (isImage(url)) {
        previewImageUrl.value = url
        previewDialogVisible.value = true
      } else {
        // 对于文档，在新窗口打开
        window.open(url, '_blank')
      }
    }

    // 判断是否为图片
    const isImage = (url: string) => {
      return /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(url)
    }

    // 获取文件名
    const getFileName = (url: string) => {
      const parts = url.split('/')
      return parts[parts.length - 1] || '文件'
    }

    // 下载模板
    const handleDownloadTemplate = () => {
      const lang = (localStorage.getItem('lang') || 'zh').toLowerCase()
      const downloadUrl =
        lang === 'en'
          ? 'https://digital-public.obs.cn-east-3.myhuaweicloud.com/guiyu-web/assets/Authorization.docx'
          : 'https://digital-public.obs.cn-east-3.myhuaweicloud.com/guiyu-web/assets/%E6%8E%88%E6%9D%83%E4%B9%A6.docx'
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = lang === 'en' ? 'Authorization.docx' : '授权书模板.docx'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }

    // 获取示例图片URL
    const getExampleImageUrl = () => {
      const lang = (localStorage.getItem('lang') || 'zh').toLowerCase()
      if (lang === 'en') {
        return require('@/assets/images/certification-demo2-en.png')
      }
      return require('@/assets/images/certification-demo2.png')
    }

    // 营业执照上传相关
    const handleBusinessLicenseExceed = (files: any) => {
      businessLicenseFileList.value = []
      const file = files[0]
      businessLicenseFileList.value = [file]
    }

    const handleBusinessLicenseUpload = async (options: any) => {
      const { file } = options
      try {
        businessLicenseUploading.value = true
        const result = await commonUpload({
          file: file.raw || file,
          level: 1,
        })
        // commonUpload 返回 ScanResult: { url, publicUrl, status, name, size, type, level }
        form.businessLicenseUrl = result.publicUrl
        businessLicenseFileList.value = [{ name: result.name, url: result.publicUrl }]
      } catch (error: any) {
        ElMessage.error(error || lang('certification.upload.imageTypeError'))
        // 上传失败时，清空相关状态，确保可以重新上传
        form.businessLicenseUrl = ''
        businessLicenseFileList.value = []
        nextTick(() => {
          if (businessLicenseUpload.value) {
            businessLicenseUpload.value.clearFiles()
          }
        })
      } finally {
        businessLicenseUploading.value = false
      }
    }

    // 删除函数
    const handleBusinessLicenseRemove = () => {
      form.businessLicenseUrl = ''
      businessLicenseUploading.value = false
      businessLicenseFileList.value = []
      nextTick(() => {
        if (businessLicenseUpload.value) businessLicenseUpload.value.clearFiles()
      })
    }

    const beforeBusinessLicenseUpload = (file: any) => {
      const isImage = file.type === 'image/jpeg' || file.type === 'image/png'
      const isLt5M = file.size / 1024 / 1024 < 5

      if (!isImage) {
        ElMessage.error(lang('certification.upload.imageTypeError'))
        return false
      }
      if (!isLt5M) {
        ElMessage.error(lang('certification.upload.imageSizeLimit'))
        return false
      }
      return true
    }

    // 授权书上传相关
    const handleAuthorLetterExceed = (files: any) => {
      authorLetterFileList.value = []
      const file = files[0]
      authorLetterFileList.value = [file]
    }

    const handleAuthorLetterUpload = async (options: any) => {
      const { file } = options
      try {
        authorLetterUploading.value = true
        const result = await commonUpload({
          file: file.raw || file,
          level: 1,
        })
        // commonUpload 返回 ScanResult: { url, publicUrl, status, name, size, type, level }
        form.authorLetterUrl = result.publicUrl
        authorLetterFileList.value = [{ name: result.name, url: result.publicUrl }]
      } catch (error: any) {
        ElMessage.error(error || lang('certification.upload.fileTypeError'))
        // 上传失败时，清空相关状态，确保可以重新上传
        form.authorLetterUrl = ''
        authorLetterFileList.value = []
        nextTick(() => {
          if (authorLetterUpload.value) {
            authorLetterUpload.value.clearFiles()
          }
        })
      } finally {
        authorLetterUploading.value = false
      }
    }

    // 删除函数
    const handleAuthorLetterRemove = () => {
      form.authorLetterUrl = ''
      authorLetterUploading.value = false
      authorLetterFileList.value = []
      nextTick(() => {
        if (authorLetterUpload.value) authorLetterUpload.value.clearFiles()
      })
    }

    // 获取当前语言
    const getCurrentLang = () => {
      if (typeof window !== 'undefined' && window.localStorage) {
        return (window.localStorage.getItem('lang') || 'zh').toLowerCase()
      }
      return 'zh'
    }

    // 获取授权书示例预览图片URL
    const getAuthorLetterPreviewUrl = () => {
      const currentLang = getCurrentLang()
      return currentLang === 'zh'
        ? require('@/assets/images/certification-origin2.png')
        : require('@/assets/images/certification-origin3.png')
    }

    // 预览示例图片
    const handlePreviewExample = (imageUrl: string) => {
      previewImageUrl.value = imageUrl
      previewDialogVisible.value = true
    }

    const beforeAuthorLetterUpload = (file: any) => {
      const isValidType =
        file.type === 'application/msword' ||
        file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        file.type === 'application/pdf'
      const isLt10M = file.size / 1024 / 1024 <= 10

      if (!isValidType) {
        ElMessage.error(lang('certification.upload.fileTypeError'))
        return false
      }
      if (!isLt10M) {
        ElMessage.error(lang('certification.upload.fileSizeLimit'))
        return false
      }
      return true
    }

    // 提交表单
    const handleSubmit = async () => {
      if (!formRef.value || typeof formRef.value.validate !== 'function') {
        ElMessage.error('表单验证失败，请重试')
        return
      }

      try {
        await formRef.value.validate()
        submitLoading.value = true

        const params = {
          corpName: form.corpName,
          businessLicenseUrl: form.businessLicenseUrl || '',
          authorLetterUrl: form.authorLetterUrl || '',
          authorLetterName: authorLetterFileList.value[0]?.name || '',
        }
        if (!params.corpName) {
          ElMessage.error(lang('certification.form.corpNameRequired'))
          return
        }
        if (!params.businessLicenseUrl) {
          ElMessage.error(lang('certification.form.businessLicenseRequired'))
          return
        }
        if (props.authorizationRequired && !params.authorLetterUrl) {
          ElMessage.error(lang('certification.form.authorLetterRequired'))
          return
        }

        const response = await api.Scrm_sys_api.srcmLogin.registerCorp({
          data: params,
          error: false,
          success: false,
        })

        if (response.success) {
          ElMessage.success(lang('certification.submit.success'))
          emit('success', response)
          handleClose()
        } else {
          ElMessage.error(response.message || lang('certification.submit.fail'))
        }
      } catch (error) {
        console.error('认证提交错误:', error)
        ElMessage.error(lang('certification.submit.fail'))
      } finally {
        submitLoading.value = false
      }
    }

    // 关闭弹窗
    const handleClose = () => {
      // 重置表单
      form.corpName = ''
      form.businessLicenseUrl = ''
      form.authorLetterUrl = ''
      businessLicenseFileList.value = []
      authorLetterFileList.value = []

      // 安全地重置表单字段
      if (formRef.value && typeof formRef.value.resetFields === 'function') {
        formRef.value.resetFields()
      }

      emit('update:visible', false)
      emit('close')
    }
    const handleOnlyClose = () => {
      // 重置表单
      form.corpName = ''
      form.businessLicenseUrl = ''
      form.authorLetterUrl = ''
      businessLicenseFileList.value = []
      authorLetterFileList.value = []

      // 安全地重置表单字段
      if (formRef.value && typeof formRef.value.resetFields === 'function') {
        formRef.value.resetFields()
      }

      emit('update:visible', false)
    }
    // 监听visible变化，重置表单
    watch(
      () => props.visible,
      (newVal) => {
        if (!newVal) {
          form.corpName = ''
          form.businessLicenseUrl = ''
          form.authorLetterUrl = ''
          businessLicenseFileList.value = []
          authorLetterFileList.value = []
        }
      },
    )

    return {
      submitLoading,
      previewDialogVisible,
      previewImageUrl,
      formRef,
      form,
      rules,
      businessLicenseFileList,
      authorLetterFileList,
      handlePreview,
      isImage,
      getFileName,
      handleDownloadTemplate,
      getExampleImageUrl,
      handleBusinessLicenseExceed,
      handleBusinessLicenseUpload,
      handleBusinessLicenseRemove,
      beforeBusinessLicenseUpload,
      handleAuthorLetterExceed,
      handleAuthorLetterUpload,
      handleAuthorLetterRemove,
      beforeAuthorLetterUpload,
      handleSubmit,
      handleClose,
      handleOnlyClose,
      lang, // 添加 lang 函数
      businessLicenseUploading,
      authorLetterUploading,
      handlePreviewExample,
      getAuthorLetterPreviewUrl,
      businessLicenseUpload,
      authorLetterUpload,
    }
  },
})
